import React, { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { InfiniteLoadingContainer } from 'components';
import { Button, CenteredLoader, useToast } from '@app/core';
import {
  SessionStatus,
  useBulkInvite,
  useInvitationQueueNextsToInvite,
  useInvitationQueueSpeedInfo,
  useSession,
  useSeveralQueuesRepartition,
} from '@app/api';
import { QueueMembersTable } from './components/QueueMembersTable';
import { useTranslation } from 'react-i18next';
import { SelectionMenu } from './components/SelectionMenu';
import classNames from 'classnames';
import { QueueSearchConfig } from './components/QueueSearchConfig';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase * 3,
    overflowX: 'auto',
  },
  buttonLine: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.marginBase * 2,
    gap: theme.marginBase,
  },
  title: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 2,
  },
  spacer: {
    flex: 1,
  },
  settingIcon: {
    fontSize: theme.iconSize.large,
    marginRight: theme.marginBase,
  },
  queueRepartitionList: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
  },
}));

interface Props {
  queueId: string;
  sessionId: string;
  toggleConfig?: () => any;
}

export const NextInvitations = ({
  queueId,
  sessionId,
  toggleConfig,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<string[]>([]);
  const toast = useToast();

  const [criteriaWeightWhitelist, setCriteriaWeightWhitelist] = useState<
    undefined | string[]
  >(undefined);

  const paginatedResult = useInvitationQueueNextsToInvite(
    queueId,
    criteriaWeightWhitelist,
  );
  const { mutateAsync: bulkInvite, isLoading: inviting } = useBulkInvite();
  const { data: session } = useSession(sessionId);
  const { data: queueConfigs } = useSeveralQueuesRepartition(queueId);
  const { data: speedInformation } = useInvitationQueueSpeedInfo(queueId);

  const members =
    paginatedResult.data?.pages?.flatMap((page) => page.data) || [];
  const isLoading = paginatedResult.isLoading;

  const onSelect = useCallback(
    (id: string) => {
      setSelected((prev) => {
        if (prev.includes(id)) {
          return prev.filter((i) => i !== id);
        }
        return [...prev, id];
      });
    },
    [setSelected],
  );

  const selectCount = (count: number) => {
    if (!members) {
      return;
    }
    setSelected(members.slice(0, count).map((m) => m.id));
  };

  const invite = async () => {
    try {
      await bulkInvite({ sessionId, participantIds: selected });
      setSelected([]);
      toast.success(t('sessions.details.invitationQueue.invite.invited'));
    } catch (e) {
      toast.exception(e);
    }
  };

  return (
    <InfiniteLoadingContainer
      infiniteQueryResponse={paginatedResult}
      className={classes.container}
    >
      <div className={classes.buttonLine}>
        {session?.status === SessionStatus.LIVE ? (
          <Button
            small
            disabled={selected.length === 0}
            onClick={invite}
            isLoading={inviting}
          >
            {t('sessions.details.invitationQueue.invite.button', {
              count: selected.length,
            })}
          </Button>
        ) : (
          <div />
        )}
        <div className={classes.spacer} />
        <SelectionMenu onSelect={selectCount} />
        <Button variant="muted" onClick={toggleConfig} color="blue" small>
          <i
            className={classNames(classes.settingIcon, 'ri-settings-3-line')}
          />
          {t('sessions.details.invitationQueue.settings.title')}
        </Button>
      </div>
      {queueConfigs?.length && queueConfigs.length > 1 && (
        <div className={classes.queueRepartitionList}>
          {queueConfigs.map((config) => (
            <QueueSearchConfig
              config={config}
              totalToInviteDaily={speedInformation?.total}
              key={config.criteriaWeightWhitelist.join('-')}
              onClick={setCriteriaWeightWhitelist}
              selectedWhiteList={criteriaWeightWhitelist}
            />
          ))}
        </div>
      )}
      <h2 className={classes.title}>
        {t('sessions.details.invitationQueue.nexts')}
      </h2>
      {members && (
        <QueueMembersTable
          members={members}
          handledFields={paginatedResult.data?.pages[0]?.handledFields}
          onSelect={onSelect}
          selected={selected}
        />
      )}
      {isLoading && <CenteredLoader />}
    </InfiniteLoadingContainer>
  );
};
